/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Building construction crane by Kieran Farr [CC-BY] (https://creativecommons.org/licenses/by/3.0/) via Poly Pizza (https://poly.pizza/m/cm5teXZ5Ctr)
*/
import { useSpring } from '@react-spring/three'
import { Center, useGLTF, useMatcapTexture } from '@react-three/drei'
import { useFrame, type ObjectMap } from '@react-three/fiber'
import { CuboidCollider, RapierRigidBody, RigidBody, vec3 } from '@react-three/rapier'
import { useEffect, useRef, useState, type JSX } from 'react'
import {
  Euler,
  Material,
  MathUtils,
  MeshMatcapMaterial,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type Mesh,
} from 'three'
import type { GLTF } from 'three-stdlib'

import { Rope } from './Rope'

type GLTFResult = GLTF &
  ObjectMap & {
    nodes: Record<string, Mesh>
    materials: Record<string, MeshStandardMaterial>
  }

type CraneProps = JSX.IntrinsicElements['group'] & {
  onMove?: (position: Vector3) => void
}

export function Crane({ onMove, ...props }: CraneProps) {
  const { nodes, materials } = useGLTF('/models/crane.glb') as GLTFResult

  const [matcap] = useMatcapTexture('7A7A7A_D9D9D9_BCBCBC_B4B4B4', 64)
  const [overriddenMaterials, setOverriddenMaterials] = useState<Record<string, Material>>()

  const boomRef = useRef<RapierRigidBody>(null!)
  const hookRef = useRef<RapierRigidBody>(null!)
  const [ropeLength, setRopeLength] = useState(0.5)

  useEffect(() => {
    setOverriddenMaterials({
      mat12: new MeshMatcapMaterial({ matcap, color: '#f3c355' }), //bulldozer color
      mat23: new MeshMatcapMaterial({ matcap, color: materials.mat23.color }),
    })
  }, [materials, matcap])

  useSpring({
    from: { boomRotation: 270 },
    to: { boomRotation: 130 },
    config: { duration: 2000 },
    onChange: v => {
      const euler = new Euler(0, MathUtils.degToRad(v.value.boomRotation), 0)
      const quaternion = new Quaternion().setFromEuler(euler)
      boomRef.current?.setRotation(quaternion, true)
    },
  })

  useSpring({
    from: { ropeLength: 0.5 },
    to: { ropeLength: 2.5 },
    config: { duration: 2000 },
    delay: 2500,
    onChange: v => setRopeLength(v.value.ropeLength),
  })

  useFrame(() => {
    if (!hookRef.current || hookRef.current.isSleeping()) return
    onMove?.(vec3(hookRef.current.translation()))
  })

  return (
    overriddenMaterials && (
      <group {...props} dispose={null}>
        <group position-y={1.85} rotation-y={MathUtils.degToRad(180)}>
          {/* base */}
          <Center>
            <mesh geometry={nodes.group1532226185.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group1751413145.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group509876920.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group533580267.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group776536491.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group1775780259.geometry} material={overriddenMaterials.mat12} />
            <mesh geometry={nodes.group307797914.geometry} material={overriddenMaterials.mat12} />
          </Center>

          {/* boom */}
          <RigidBody colliders={false} ref={boomRef} position={[0, 2, 0]} type="kinematicPosition">
            <CuboidCollider args={[1.8, 0.25, 0.5]} />
            <group>
              <Center position={[-0.71, 0.45, -0.02]}>
                <mesh
                  geometry={nodes.group1664925870.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group235962895.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group592909958.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group366854728.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group391839252.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group974517053.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group998573847.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1947799659.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group672087033.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1296863136.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group87113771.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group495680477.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group397477980.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1811979738.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group473507205.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1966155105.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1299017553.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group626598083.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group445506964.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1437149629.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group2037050405.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group760901227.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1851785307.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1780168133.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1201660380.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group906026898.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1952948338.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1541000995.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group446689335.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1905374652.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group927672832.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group370920667.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group2053379505.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1733420862.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group596657296.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1646145142.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group219201849.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group795856984.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group880570556.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group569365418.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1190423099.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group1975274342.geometry}
                  material={overriddenMaterials.mat12}
                />
                <mesh
                  geometry={nodes.group489823944.geometry}
                  material={overriddenMaterials.mat12}
                />

                {/* cables */}
                <group>
                  <mesh
                    geometry={nodes.group1841255697.geometry}
                    material={overriddenMaterials.mat23}
                  />
                  <mesh
                    geometry={nodes.group1578632476.geometry}
                    material={overriddenMaterials.mat23}
                  />
                  <mesh
                    geometry={nodes.group1044784262.geometry}
                    material={overriddenMaterials.mat23}
                  />
                  <mesh
                    geometry={nodes.group2001122342.geometry}
                    material={overriddenMaterials.mat23}
                  />
                </group>

                {/* pulley */}
                <group>
                  <mesh
                    geometry={nodes.group479716467.geometry}
                    material={overriddenMaterials.mat23}
                  />

                  {/* <mesh geometry={nodes.group76975118.geometry} material={overriddenMaterials.mat23} /> */}
                </group>
              </Center>
            </group>
          </RigidBody>

          <RigidBody
            position={[-1.45, 0, 0]}
            ref={hookRef}
            colliders="cuboid"
            gravityScale={30}
            linearDamping={8}
            angularDamping={8}
          >
            <mesh>
              <icosahedronGeometry args={[0.05, 2]} />
              <meshMatcapMaterial matcap={matcap} color="brown" />
            </mesh>
          </RigidBody>

          <Rope
            key={ropeLength}
            start={boomRef}
            end={hookRef}
            color="gray"
            startAnchor={[-1.8, -0.25, 0]}
            radius={0.015}
            length={ropeLength}
          />
        </group>
      </group>
    )
  )
}

useGLTF.preload('/models/crane.glb')
